import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PlanContext, SubscriptionDto } from '@welynk/types';
import { BillingApiService, type CheckoutRequest } from '@/lib/services/billing-api';
import { useUIStore } from '@/store/ui-store';
import { apiErrorMessage } from '@/lib/api-error-utils';

export const billingKeys = {
  plans: (context?: PlanContext) => ['billing', 'plans', context ?? 'all'] as const,
  overview: (organizationId?: string) =>
    ['billing', 'overview', organizationId ?? 'personal'] as const,
  invoices: (organizationId?: string, page?: number) =>
    ['billing', 'invoices', organizationId ?? 'personal', page ?? 1] as const,
};

export function usePlans(context?: PlanContext) {
  return useQuery({
    queryKey: billingKeys.plans(context),
    queryFn: () => BillingApiService.getPlans(context),
    staleTime: 5 * 60_000,
  });
}

export function useBillingOverview(organizationId?: string, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: billingKeys.overview(organizationId),
    queryFn: () => BillingApiService.getOverview(organizationId),
    staleTime: 30_000,
    enabled: options.enabled ?? true,
  });
}

export function useBillingInvoices(organizationId?: string, page = 1, pageSize = 20) {
  return useQuery({
    queryKey: billingKeys.invoices(organizationId, page),
    queryFn: () => BillingApiService.getInvoices({ organizationId, page, pageSize }),
    staleTime: 30_000,
  });
}

/**
 * Stripe hosted Checkout flow: create a session, then hand the browser to
 * Stripe. The success/cancel URLs bring the user back to the calling page.
 */
export function useCheckout() {
  const { addToast } = useUIStore();
  return useMutation({
    mutationFn: (body: CheckoutRequest) => BillingApiService.createCheckout(body),
    onSuccess: (session) => {
      window.location.assign(session.checkoutUrl);
    },
    onError: (error: unknown) => {
      addToast({ type: 'error', message: checkoutErrorMessage(error) });
    },
  });
}

export function useCancelSubscription(organizationId?: string) {
  const queryClient = useQueryClient();
  const { addToast } = useUIStore();
  return useMutation({
    mutationFn: (immediate?: boolean) =>
      BillingApiService.cancelSubscription(organizationId, immediate ?? false),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: billingKeys.overview(organizationId) });
      addToast({
        type: 'success',
        message: 'Subscription will end at the close of the current billing period.',
      });
    },
    onError: (error: unknown) => addToast({ type: 'error', message: checkoutErrorMessage(error) }),
  });
}

export function useResumeSubscription(organizationId?: string) {
  const queryClient = useQueryClient();
  const { addToast } = useUIStore();
  return useMutation({
    mutationFn: () => BillingApiService.resumeSubscription(organizationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: billingKeys.overview(organizationId) });
      addToast({ type: 'success', message: 'Your subscription will continue — welcome back!' });
    },
    onError: (error: unknown) => addToast({ type: 'error', message: checkoutErrorMessage(error) }),
  });
}

export function useChangePlan() {
  const queryClient = useQueryClient();
  const { addToast } = useUIStore();
  return useMutation({
    mutationFn: (body: CheckoutRequest) => BillingApiService.changePlan(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billing'] });
      addToast({ type: 'success', message: 'Plan updated. Prorated charges apply automatically.' });
    },
    onError: (error: unknown) => addToast({ type: 'error', message: checkoutErrorMessage(error) }),
  });
}

/** Live coupon check before checkout — avoids a failed checkout attempt for a bad code */
export function useCouponPreview(code: string, planId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['billing', 'coupon-preview', code, planId],
    queryFn: () => BillingApiService.previewCoupon(code, planId!),
    enabled: enabled && code.trim().length > 0 && Boolean(planId),
    staleTime: 60_000,
    retry: false,
  });
}

/** Opens the Stripe-hosted Customer Portal for payment method / invoice self-service */
export function usePortalSession(organizationId?: string) {
  const { addToast } = useUIStore();
  return useMutation({
    mutationFn: (returnUrl: string) =>
      BillingApiService.createPortalSession(returnUrl, organizationId),
    onSuccess: (session) => {
      window.location.assign(session.checkoutUrl);
    },
    onError: (error: unknown) => addToast({ type: 'error', message: checkoutErrorMessage(error) }),
  });
}

const ACTIVATION_POLL_INTERVAL_MS = 2000;
const ACTIVATION_TIMEOUT_MS = 20_000;

export interface CheckoutActivationState {
  subscription: SubscriptionDto | null;
  /** The subscription reflects the new paid plan — safe to show the congrats content */
  ready: boolean;
  /** Still not active after ~20s — webhook is unusually slow; show a fallback */
  timedOut: boolean;
  isLoading: boolean;
}

/**
 * Activates the subscription after a Stripe Checkout redirect. Two paths
 * race to the same result:
 *  1. Synchronous reconcile (preferred): pulls the checkout session from
 *     Stripe directly and applies it immediately — works even if no webhook
 *     is configured/reachable at all.
 *  2. Polling billing overview (fallback): catches the case where reconcile
 *     itself fails but the `checkout.session.completed` webhook still lands
 *     a few seconds later.
 */
export function useCheckoutActivation(
  organizationId?: string,
  sessionId?: string | null
): CheckoutActivationState {
  const [ready, setReady] = useState(false);
  const [elapsedMs, setElapsedMs] = useState(0);
  const queryClient = useQueryClient();
  const reconcileAttempted = useRef(false);

  const overviewQuery = useQuery({
    queryKey: billingKeys.overview(organizationId),
    queryFn: () => BillingApiService.getOverview(organizationId),
    refetchInterval: ready ? false : ACTIVATION_POLL_INTERVAL_MS,
  });

  useEffect(() => {
    if (!sessionId || reconcileAttempted.current) return;
    reconcileAttempted.current = true;
    BillingApiService.reconcileCheckout(sessionId, organizationId)
      .then(() => {
        queryClient.invalidateQueries({ queryKey: billingKeys.overview(organizationId) });
      })
      .catch(() => {
        // Non-fatal — the checkout.session.completed webhook (if configured)
        // is the fallback path, caught by the polling below.
      });
  }, [sessionId, organizationId, queryClient]);

  const subscription = overviewQuery.data?.subscription ?? null;

  useEffect(() => {
    if (
      subscription &&
      subscription.status !== 'INCOMPLETE' &&
      subscription.plan.priceMonthly != null
    ) {
      setReady(true);
    }
  }, [subscription]);

  useEffect(() => {
    if (ready) return;
    const id = setInterval(
      () => setElapsedMs((ms) => ms + ACTIVATION_POLL_INTERVAL_MS),
      ACTIVATION_POLL_INTERVAL_MS
    );
    return () => clearInterval(id);
  }, [ready]);

  return {
    subscription,
    ready,
    timedOut: !ready && elapsedMs >= ACTIVATION_TIMEOUT_MS,
    isLoading: overviewQuery.isLoading,
  };
}

function checkoutErrorMessage(error: unknown): string {
  return apiErrorMessage(error, 'Something went wrong with billing. Please try again.');
}
